import { useState } from "react";
import { useCreate, useNotify } from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

import type { Agency } from "../types";
import { createAgencyErrorMessage } from "./createAgencyErrorMessage";

export const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;

/** "VDB Luxury Properties " -> "vdb-luxury-properties" (DB slug shape). */
export const slugify = (name: string): string =>
  name
    .toLowerCase()
    .normalize("NFD")
    // Strip combining diacritics left by NFD (é -> e).
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);

const normalizeWebsite = (input: string): string | null => {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

export const CreateAgencyDialog = ({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}) => {
  const notify = useNotify();
  const [create, { isPending: isSaving }] = useCreate();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [website, setWebsite] = useState("");

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const slugValid = SLUG_RE.test(effectiveSlug);

  const reset = () => {
    setName("");
    setSlug("");
    setSlugTouched(false);
    setWebsite("");
  };

  const save = () => {
    if (!name.trim() || !slugValid) {
      return;
    }
    create(
      "agencies",
      {
        data: {
          name: name.trim(),
          slug: effectiveSlug,
          website_url: normalizeWebsite(website),
          status: "active",
        },
      },
      {
        onSuccess: (record: Agency) => {
          notify(`Created ${record.name}.`, { type: "info" });
          onCreated(String(record.id));
          onOpenChange(false);
          reset();
        },
        onError: (error: unknown) => {
          notify(createAgencyErrorMessage(error, effectiveSlug), {
            type: "warning",
          });
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) {
          reset();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New agency</DialogTitle>
          <DialogDescription>
            A real-estate tenant. The slug is the stable API key carried on
            pipeline envelopes and dashboard events — choose it once.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <Input
            placeholder="Agency name — e.g. VDB Luxury Properties"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Input
            placeholder="slug — e.g. vdb-luxury-properties"
            value={effectiveSlug}
            onChange={(event) => {
              setSlugTouched(true);
              setSlug(slugify(event.target.value));
            }}
            className="font-mono"
          />
          {!slugValid && effectiveSlug.length > 0 && (
            <p className="text-sm text-destructive">
              Slug must be 3–40 chars of lowercase letters, digits and dashes.
            </p>
          )}
          <Input
            placeholder="Website (optional) — used for brand extraction"
            value={website}
            onChange={(event) => setWebsite(event.target.value)}
          />
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={!name.trim() || !slugValid || isSaving}
          >
            Create agency
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
